/**
 * Where propagation material came from.
 *
 * Whoever propagates declares the source before they propagate — a nursery on
 * each offer it puts on sale, a grower on each self-propagation request. It is
 * the double check that the trees produced under a licence are the cultivar
 * the licence is for. Mirrors `HHNodeServer/src/services/propagationMaterialService.js`.
 */

export type MaterialKind = 'scion' | 'rootstock' | 'interstem';

export type MaterialSourceType =
  | 'licensor'
  | 'certified_scheme'
  | 'own_stock'
  | 'other_propagator'
  | 'other';

/** What a propagator declares. */
export interface MaterialSourceInput {
  material_kind: MaterialKind;
  source_type: MaterialSourceType;
  source_org_id?: number | null;
  source_name: string;
  source_reference?: string | null;
  notes?: string | null;
}

/** A declaration as stored. */
export interface MaterialSource extends MaterialSourceInput {
  id: number;
  cultivar_id: number;
  propagator_org_id: number;
  nursery_offer_id: number | null;
  self_propagation_request_id: number | null;
  source_org_name?: string | null;
  created_at: string;
  updated_at: string;
}

/** A declaration in the rights holder's review list for a cultivar. */
export interface DeclaredMaterialSource extends MaterialSource {
  propagator_name: string;
  declared_on: 'offer' | 'self_propagation';
  offer_season_year: number | null;
  offer_status: string | null;
  request_season_year: number | null;
  request_status: string | null;
}

export const MATERIAL_KIND_LABELS: Record<MaterialKind, string> = {
  scion: 'Scion / budwood',
  rootstock: 'Rootstock',
  interstem: 'Interstem',
};

export const SOURCE_TYPE_LABELS: Record<MaterialSourceType, string> = {
  licensor: 'Supplied by the rights holder',
  certified_scheme: 'Certified budwood scheme',
  own_stock: 'Our own mother trees or block',
  other_propagator: 'Another nursery or grower',
  other: 'Other (describe)',
};

/** What to type in "source", by type — so "own stock" gets asked which stock. */
export const SOURCE_NAME_PLACEHOLDERS: Record<MaterialSourceType, string> = {
  licensor: 'Who at the rights holder sent it',
  certified_scheme: 'Scheme name',
  own_stock: 'Which block, rows or mother trees',
  other_propagator: 'Nursery or grower name',
  other: 'Who or where',
};

export function blankMaterialSource(kind: MaterialKind = 'scion'): MaterialSourceInput {
  return { material_kind: kind, source_type: 'certified_scheme', source_name: '', source_reference: '', notes: '' };
}

/**
 * The same rule the server applies before a declaration is relied on: at least
 * one scion source, every row naming where, and "other" described.
 * Returns the problem in words, or null when there is none.
 */
export function materialSourcesProblem(
  sources: MaterialSourceInput[],
  { requireScion }: { requireScion: boolean }
): string | null {
  for (let i = 0; i < sources.length; i++) {
    const s = sources[i];
    if (!s.source_name.trim()) return `Source ${i + 1}: say who or where the material came from.`;
    if (s.source_type === 'other' && !(s.notes || '').trim()) {
      return `Source ${i + 1}: describe what the "other" source is.`;
    }
  }
  if (requireScion && !sources.some(s => s.material_kind === 'scion')) {
    return 'Record where the scion material (budwood or scion wood) is coming from — it is the licensed material itself.';
  }
  return null;
}
